import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
    Box, IconButton, Link, MenuItem, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';

import { JobTypes } from 'BMapsSrc/jobs/JobDefinition';
import { useDispatch } from 'BMapsRedux';
import { createJob } from 'BMapsRedux/jobs/thunks';
import { DialogCancelButton, DialogPrimaryButton, DialogSubmitButton } from 'BMapsUI/common/DialogActionButtons';
import DialogContentSection, { DialogContentWithTooltip } from 'BMapsUI/common/DialogContentSection';
import Select from 'BMapsUI/common/Select';
import { SidePanelSection } from 'BMapsUI/info_display/SidePanel';
import { CompoundTable } from 'BMapsUI/tables/CompoundTable';
import { TextArea } from 'BMapsUI/common/TextFields';
import { Loader } from 'BMapsSrc/Loader';
import { conditionalArrayItem, createUniqueKey } from 'BMapsSrc/util/js_utils';
import ModalPane from 'BMapsSrc/ui/ModalPane';
import { WorkingIndicator } from 'BMapsSrc/ui/ui_utils';
import BMapsTooltip from 'BMapsSrc/ui/BMapsTooltip';
import { AccessTime, Edit, Warning } from '@mui/icons-material';
import { EditInPlaceInput, TextOrTooltip } from 'BMapsSrc/ui/tables/TableCells';
import { GridClearIcon } from '@mui/x-data-grid';
import { wrapForDisabledTooltip } from 'BMapsSrc/ui/common/helpers';
import { isSmilesLine } from 'BMapsSrc/util/mol_format_utils';
import useCompoundUpdateSubscription from 'BMapsSrc/ui/common/helpers/useCompoundUpdateSubscription';
import { BasicTextField } from '../../SimulationPane/Components';
import { ImportErrors, PlaceHolder } from '../../ImportPane/import_components';
import { openScreeningSidePanel } from '../ScreeningSidePanel';

const libraryOptions = [
    { key: 'all-human-kinases', label: 'Human Kinases - All (536)' },
    { key: 'human-kinase-subset', label: 'Human Kinases - Diverse Subset (50)' },
    ...conditionalArrayItem(Loader.AllowLabFeatures, { key: '3-kinases', label: 'Human Kinases - Test 3 (BRAF, p38, JAK3)' }),
];

const screenTypeOptions = [
    {
        key: 'boltz2_folding', label: 'Boltz-2 Folding', jobtype: 'folding', subtype: 'Boltz-2',
    },
];

export default function NewScreen({
    initData, selected, setHighlightedJobId,
}) {
    const workspaceCompounds = useCompoundUpdateSubscription();
    const [chosenLibrary, setChosenLibrary] = useState(
        libraryOptions.find((item) => item.key === initData.library)
        || libraryOptions[0]
    );
    const [chosenScreenType, setChosenScreenType] = useState(
        screenTypeOptions.find((item) => item.key === initData.screenType)
        || screenTypeOptions[0]
    );
    const [ligandSmilesText, setLigandSmilesText] = useState(initData.smiles || '');
    const [newJobName, setNewJobName] = useState(initData.jobName || '');
    const dispatch = useDispatch();

    const [stagedInputData, setStagedInputData] = useState(null);
    const [stagedParametersData, setStagedParametersData] = useState(null);

    const [confirmation, setConfirmation] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingValues, setEditingValues] = useState({});
    const [submitErrors, setSubmitErrors] = useState();
    const [confirmationErrors, setConfirmationErrors] = useState();

    // clear confirmation errors if no issues
    useEffect(() => {
        if (!confirmationErrors) return;
        const count = stagedInputData.length;
        const uniqueSmiles = _.uniqBy(stagedInputData, (item) => item.smiles).length;
        if (count === uniqueSmiles) {
            setConfirmationErrors(null);
        }
    }, [confirmationErrors, stagedInputData]);

    function stageScreeningJob() {
        // id is not editable, so it remains unique in table.
        const compoundList = initData.selectedCompounds?.map((item) => ({
            smiles: item.getUnnamedSmiles(),
            id: item.resSpec,
            name: item.resSpec,
            key: createUniqueKey(),
        })) || [];

        const smilesList = ligandSmilesText.split('\n').filter(Boolean);
        for (const smilesString of smilesList) {
            const entry = smilesString.split(/\s+/);
            const name = entry.slice(1).join(' ');
            const key = createUniqueKey();
            if (compoundList.find(({ smiles }) => smiles === entry[0])) {
                continue;
            }
            compoundList.push({ smiles: entry[0], key, name });
        }
        setStagedInputData([
            ...compoundList,
        ]);
        setStagedParametersData({
            library: {
                propertyKey: 'library',
                propertyLabel: 'Library',
                value: chosenLibrary,
                label: chosenLibrary.label,
            },
            screen_type: {
                propertyKey: 'screen_type',
                propertyLabel: 'Screen Type',
                value: chosenScreenType,
                label: chosenScreenType.label,
            },
            jobName: {
                propertyKey: 'jobName',
                propertyLabel: 'Job Name',
                value: newJobName,
                label: newJobName,
                editable: true,
            },
        });
    }

    async function submitForScreening() {
        try {
            setIsSubmitting(true);

            // we convert the name to id when submitting.
            // duplicate name values are prevented due to button being disabled.
            const input = stagedInputData.map((item) => ({
                smiles: item.smiles,
                id: item.name,
            }));
            const parameters = stagedParametersData;
            const { results, error } = await dispatch(
                createJob(JobTypes.LIBRARY_SCREEN, {
                    compounds: input,
                    library: { id: parameters.library.value.key },
                    screen_type: {
                        jobtype: parameters.screen_type.value.jobtype,
                        subtype: parameters.screen_type.value.subtype,
                    },
                    jobName: parameters.jobName.value,
                })
            );

            if (error) {
                setSubmitErrors([error]);
                return false;
            }

            if (results) {
                setNewJobName('');
                setLigandSmilesText('');
                setStagedInputData(null);
                setStagedParametersData(null);
                setHighlightedJobId(results.jobid);
                openScreeningSidePanel('screen_jobs_tab', { ...initData, selectedCompounds: [] });
                return true;
            } else {
                setSubmitErrors(['No results returned from screening request.']);
                return false;
            }
        } catch (ex) {
            setSubmitErrors([`There was an error submitting the folding request. ${ex}`]);
            return false;
        } finally {
            setIsSubmitting(false);
        }
    }

    if (!selected) return null;
    const ligandSmilesCount = ligandSmilesText.split('\n').filter(Boolean).length;

    function disableSubmitButtonMessage() {
        const compoundCount = initData.selectedCompounds?.length || 0;
        if (ligandSmilesCount < 1 && compoundCount < 1) {
            return 'Add compounds or smiles strings to submit for screening';
        }
        if (!chosenLibrary || !chosenScreenType) {
            return 'Select a library and screen type to submit for screening';
        }
        return false;
    }
    return (
        <>
            <DialogContentWithTooltip
                title="Compound(s)"
                disablePadding
                style={{ width: '100%' }}
                ContainerProps={{ sx: { overflow: 'unset !important' } }}
                collapsible
                defaultExpand
            >
                {workspaceCompounds?.length > 0 && (
                    <CompoundsDescription
                        compounds={initData.selectedCompounds}
                        setCheckedItems={(selectedCompounds) => {
                        // The props for the screeningSidePanel are updated here.
                        // It is done this way instead of updating state directly.
                        // Selected Compounds is also controlled externally,
                        // so this avoids some complications.
                            openScreeningSidePanel('new_screen_tab', { ...initData, selectedCompounds });
                        }}
                    />
                )}

                <DialogContentSection
                    titleVariant="subtitle1"
                    title={
                        ligandSmilesCount > 0
                            ? `Smiles (${ligandSmilesCount} entries)`
                            : 'Smiles'
                    }
                    collapsible={workspaceCompounds?.length > 0}
                    defaultExpand
                >
                    <Box sx={{ margin: '1rem .5rem 0 .5rem' }}>

                        <PlaceHolder $show={ligandSmilesText === ''}>
                            <>
                                Enter SMILES, one per line.
                                <br />
                                Optional space-separated compound names can be used.
                            </>
                        </PlaceHolder>
                        <TextArea
                            autoFocus
                            multiline
                            minRows={1}
                            value={ligandSmilesText}
                            onChange={(e) => setLigandSmilesText(e.target.value)}
                            sx={{
                                '& textarea': {
                                    resize: 'vertical',
                                    minHeight: '3rem',
                                },
                            }}
                        />

                    </Box>
                </DialogContentSection>
            </DialogContentWithTooltip>
            <ScreenControls
                library={chosenLibrary}
                setLibrary={setChosenLibrary}
                screenType={chosenScreenType}
                setScreenType={setChosenScreenType}
                jobName={newJobName}
                setJobName={setNewJobName}
            />
            <BMapsTooltip title={disableSubmitButtonMessage()}>
                {wrapForDisabledTooltip(
                    <DialogPrimaryButton
                        onClick={() => {
                            stageScreeningJob();
                            setConfirmation(true);
                        }}
                        disabled={disableSubmitButtonMessage()}
                        sx={{ width: '100%' }}
                    >
                        Submit for Screening
                    </DialogPrimaryButton>
                )}
            </BMapsTooltip>
            <span style={{ fontStyle: 'italic' }}>
                Screening operations run as jobs outside your BMaps session.
                <br />
                The results will be available in the
                {' '}
                <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                    component="button"
                    onClick={() => openScreeningSidePanel('screen_jobs_tab')}
                    sx={{ color: 'var(--marketing-blue)' }}
                >
                    Screening Jobs
                </Link>
                {' '}
                tab.
            </span>

            {confirmation && (
                <NewScreenConfirmationDialog
                    open={confirmation}
                    close={() => {
                        setConfirmation(false);
                        setConfirmationErrors(null);
                        setEditingValues({});
                    }}
                    submit={submitForScreening}
                    isSubmitting={isSubmitting}
                    editingValues={editingValues}
                    confirmationErrors={confirmationErrors}
                >
                    <Box sx={{ overflow: 'auto' }}>
                        <DialogContentSection
                            title="Screening Input"
                            titleVariant="subtitle1"
                        >
                            <ScreeningInputTable
                                data={stagedInputData}
                                updateData={(newCompounds) => {
                                    setStagedInputData(newCompounds);
                                }}
                                editingValues={editingValues}
                                setEditingValues={setEditingValues}
                                setConfirmationErrors={setConfirmationErrors}
                            />
                        </DialogContentSection>
                        <DialogContentSection
                            title="Parameters"
                            titleVariant="subtitle1"
                            disablePaddingBottom
                        >
                            <ParameterConfirmationTable
                                data={stagedParametersData}
                                editingValues={editingValues}
                                setEditingValues={setEditingValues}
                                updateData={(newParameters) => {
                                    setStagedParametersData({
                                        ...stagedParametersData,
                                        ...newParameters,
                                    });
                                }}
                            />
                        </DialogContentSection>
                        <EstimatedTime
                            screenTypeKey={stagedParametersData?.screen_type?.value?.key}
                            libraryKey={stagedParametersData?.library?.value?.key}
                            compoundCount={stagedInputData?.length}
                        />
                    </Box>
                </NewScreenConfirmationDialog>
            )}
            {submitErrors && <ImportErrors errors={submitErrors} /> }
        </>
    );
}

function ParameterConfirmationTable({
    data, updateData, editingValues, setEditingValues,
}) {
    return (
        <TableContainer sx={{ marginBottom: '1rem' }}>
            <Table aria-label="Parameter listing" size="small">
                <TableBody>
                    {Object.entries(data)
                        .filter(([, parameter]) => parameter?.propertyKey !== undefined)
                        .map(([key, parameter]) => (
                            <ParameterRow
                                key={key}
                                propertyKey={parameter.propertyKey}
                                propertyLabel={parameter.propertyLabel}
                                value={parameter.value}
                                label={parameter.label}
                                editable={parameter.editable}
                                updateData={updateData}
                                editingValues={editingValues}
                                setEditingValues={setEditingValues}
                            />
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function ParameterRow({
    propertyKey, label, value, updateData, editable, propertyLabel,
    editingValues, setEditingValues,
}) {
    return (
        <TableRow>
            <TableCell
                component="th"
                scope="row"
                align="left"
                sx={{ width: '20%' }}
            >
                {propertyLabel}
            </TableCell>
            {editable ? (
                <TableCell align="left">
                    { !editingValues[propertyKey] ? (
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <BMapsTooltip title="Edit Compound Name">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setEditingValues({
                                            ...editingValues,
                                            [propertyKey]: true,
                                        });
                                    }}
                                    sx={{ marginRight: '.5rem' }}
                                >
                                    <Edit />
                                </IconButton>
                            </BMapsTooltip>
                            <TextOrTooltip
                                text={value}
                                limit={40}
                            />
                        </Box>
                    ) : (
                        <EditInPlaceInput
                            Component={TextArea}
                            value={value}
                            setValue={(val) => {
                                updateData({
                                    [propertyKey]: {
                                        propertyKey,
                                        propertyLabel,
                                        label: val,
                                        value: val,
                                        editable,
                                    },
                                });
                                setEditingValues({
                                    ...editingValues,
                                    [propertyKey]: false,
                                });
                            }}
                            stopEditing={() => {
                                setEditingValues({
                                    ...editingValues,
                                    [propertyKey]: false,
                                });
                            }}
                            componentProps={{
                                size: 'small',
                                sx: {
                                    paddingLeft: '2.5rem',
                                },
                            }}
                        />
                    )}
                </TableCell>
            ) : (
                <TableCell align="left" sx={{ paddingLeft: '3.6rem' }}>
                    {label}
                </TableCell>
            )}
        </TableRow>
    );
}

function NewScreenConfirmationDialog({
    open, close, submit, disabled, isSubmitting, editingValues, children,
    confirmationErrors,
}) {
    const isEditingValue = Object.values(editingValues).some((value) => {
        if (typeof value === 'object') {
            return Object.values(value).some((val) => val);
        }
        return value;
    });

    const submitTooltip = isEditingValue ? 'Confirm changes before submitting' : undefined;
    return (
        <ModalPane
            visible={open && !disabled}
            title="Confirm Screening Request"
            close={close}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-evenly',
            }}
            DialogProps={{
                onKeyUp: (e) => {
                    if (e.key === 'Escape') {
                        e.stopPropagation();
                    }
                },
            }}
        >
            <Box sx={{ marginTop: '1rem', overflow: 'auto' }}>
                {children}
            </Box>
            <Box sx={{
                display: 'flex', flexDirection: 'row', gap: '1rem', padding: '.5rem 0',
            }}
            >
                <BMapsTooltip title={submitTooltip}>
                    {wrapForDisabledTooltip(
                        <DialogSubmitButton
                            onClick={async () => { await submit(); close(); }}
                            sx={{ flexGrow: 1 }}
                            disabled={
                                isSubmitting
                                || isEditingValue
                                || confirmationErrors?.length > 0
                            }
                        >
                            {isSubmitting ? <WorkingIndicator /> : 'Submit'}
                        </DialogSubmitButton>,
                        { WrapperProps: { style: { flexGrow: 1, display: 'flex' } } }
                    )}
                </BMapsTooltip>
                <DialogCancelButton onClick={close} sx={{ flexGrow: 1 }}>
                    Cancel
                </DialogCancelButton>
            </Box>
            {confirmationErrors && <ImportErrors errors={confirmationErrors} /> }

        </ModalPane>
    );
}
function ScreeningInputTable({
    data, updateData, editingValues, setEditingValues, setConfirmationErrors,
}) {
    if (data?.length === 0) return false;

    function removeCompound(index) {
        data.splice(index, 1);
        updateData([...data]);
    }
    function updateCompound(index, { name, smiles }) {
        if (name !== undefined) data[index].name = name;
        if (smiles !== undefined) data[index].smiles = smiles;
        updateData([...data]);
    }

    return (
        <TableContainer component={Paper} sx={{ maxHeight: '20rem' }}>
            <Table stickyHeader sx={{ minWidth: 650 }} aria-label="Screening input items table" size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Compound Name</TableCell>
                        <TableCell align="left">Smiles</TableCell>
                        <TableCell align="right" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((compound, index) => (
                        <ScreeningInputRow
                            key={`sequence-${compound.key}`}
                            editingValues={editingValues}
                            setEditingValues={setEditingValues}
                            compound={compound}
                            compounds={data}
                            index={index}
                            removeCompound={removeCompound}
                            updateCompound={updateCompound}
                            setConfirmationErrors={setConfirmationErrors}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function ScreeningInputRow({
    compound, index, removeCompound, updateCompound, compounds, editingValues, setEditingValues,
    setConfirmationErrors,
}) {
    const errorAlert = useMemo(() => {
        const errorMessage = [];
        if (!isSmilesLine(compound.smiles)) {
            errorMessage.push('Invalid smiles sequence.');
        }
        if (compounds.filter((c) => c.smiles === compound.smiles).length > 1) {
            errorMessage.push('Duplicate smiles sequence.');
        }
        return errorMessage;
    }, [compounds, compound]);

    return (
        <TableRow
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        >
            <TableCell align="left">
                { !editingValues[compound.key]?.name ? (
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <BMapsTooltip title="Edit Compound Name">
                            <IconButton
                                onClick={() => setEditingValues({
                                    ...editingValues,
                                    [compound.key]: {
                                        ...editingValues[compound.key],
                                        name: true,
                                    },
                                })}
                            >
                                <Edit />
                            </IconButton>
                        </BMapsTooltip>
                        <TextOrTooltip
                            text={compound.name}
                            limit={40}
                        />
                    </Box>
                ) : (
                    <EditInPlaceInput
                        Component={TextArea}
                        value={compound.name}
                        setValue={(value) => {
                            setEditingValues({
                                ...editingValues,
                                [compound.key]: {
                                    ...editingValues[compound.key],
                                    name: false,
                                },
                            });
                            updateCompound(index, { name: value });
                        }}
                        stopEditing={() => {
                            setEditingValues({
                                ...editingValues,
                                [compound.key]: {
                                    ...editingValues[compound.key],
                                    name: false,
                                },
                            });
                        }}
                    />
                )}
            </TableCell>
            <TableCell align="left">
                { !editingValues[compound.key]?.smiles ? (
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <BMapsTooltip title="Edit component data">
                            <IconButton
                                onClick={() => {
                                    setEditingValues({
                                        ...editingValues,
                                        [compound.key]: {
                                            ...editingValues[compound.key],
                                            smiles: true,
                                        },
                                    });
                                }}
                            >
                                <Edit />
                            </IconButton>
                        </BMapsTooltip>
                        <TextOrTooltip
                            text={compound.smiles}
                            limit={40}
                        />
                    </Box>
                ) : (
                    <EditInPlaceInput
                        Component={TextArea}
                        value={compound.smiles}
                        setValue={(value) => {
                            setEditingValues({
                                ...editingValues,
                                [compound.key]: {
                                    ...editingValues[compound.key],
                                    smiles: false,
                                },
                            });
                            if (compounds.find((cmpd) => cmpd.smiles === value)) {
                                setConfirmationErrors([
                                    'All compounds must have unique smiles to be submitted.',
                                ]);
                            }
                            updateCompound(index, { smiles: value });
                        }}
                        stopEditing={() => {
                            setEditingValues({
                                ...editingValues,
                                [compound.key]: {
                                    ...editingValues[compound.key],
                                    smiles: false,
                                },
                            });
                        }}
                    />
                )}
            </TableCell>
            <TableCell align="right">
                {errorAlert.length > 0 && (
                    <BMapsTooltip title={errorAlert.map((e) => (<div key={e}>{e}</div>))}>
                        <IconButton sx={{ color: 'red' }}><Warning /></IconButton>
                    </BMapsTooltip>
                )}
                {compounds.length > 1 && (
                    <BMapsTooltip title="Remove component from screening input">
                        <IconButton onClick={() => removeCompound(index)}>
                            <GridClearIcon />
                        </IconButton>
                    </BMapsTooltip>
                )}
            </TableCell>
        </TableRow>
    );
}

function CompoundsDescription({ compounds, setCheckedItems }) {
    const selectedCount = compounds?.length || 0;

    return (
        <DialogContentSection
            title={
                selectedCount > 0
                    ? `Workspace Compounds (${selectedCount} selected)`
                    : 'Workspace Compounds'
            }
            titleVariant="subtitle1"
        >
            <SidePanelSection>
                <CompoundTable
                    setCheckedCompounds={setCheckedItems}
                    useUniqueCompounds
                    checkboxSelection
                    itemSelector
                    initialCheckedCompounds={compounds}
                />
            </SidePanelSection>
        </DialogContentSection>
    );
}

function ScreenControls({
    library, setLibrary, screenType, setScreenType, jobName, setJobName,
}) {
    return (
        <DialogContentWithTooltip
            title="Screen Options"
            ComponentProps={{ sx: { width: '100%' } }}
            ContainerProps={{ sx: { overflow: 'unset !important' } }}
            collapsible
            defaultExpand
        >
            <Select
                label="Library"
                value={library.key}
                SelectProps={{
                    native: false,
                }}
                sx={{ width: '100%' }}
            >
                {
                    libraryOptions.map((option) => (
                        <MenuItem
                            key={option.key}
                            value={option.key}
                            onClick={() => {
                                setLibrary(option);
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))
                }
            </Select>
            <Select
                label="Screen Type"
                value={screenType.key}
                SelectProps={{
                    native: false,
                }}
                sx={{ width: '100%', marginTop: '1rem' }}
            >
                {
                    screenTypeOptions.map((option) => (
                        <MenuItem
                            key={option.key}
                            value={option.key}
                            onClick={() => {
                                setScreenType(option);
                            }}
                        >
                            {option.label}
                        </MenuItem>
                    ))
                }
            </Select>
            <BasicTextField
                labelText="Job Name (optional)"
                value={jobName}
                title="Job Name (optional)"
                required={false}
                onChange={(evt) => setJobName(evt.target.value)}
            />
        </DialogContentWithTooltip>
    );
}

/**
 * Return a string with a time estimate for a screening job,
 * based on the screen type, library, and compound count.
 * @param {string} screenTypeKey
 * @param {string} libraryKey
 * @param {number} compoundCount
 * @returns {string?}
 */
function estimateTime(screenTypeKey, libraryKey, compoundCount) {
    if (!compoundCount || compoundCount < 1) return null;
    if (screenTypeKey === 'boltz2_folding') {
        // Estimate 4 min per fold (running 5 folds at once)
        // This does not consider other users running jobs, which would increase the time.
        const PER_KINASE_ESTIMATES = {
            'all-human-kinases': { estimate: 8, format(x) { return `${x}+ hr${x !== 1 ? 's' : ''}`; } },
            'human-kinase-subset': { estimate: 1, format(x) { return `~${x} hr${x !== 1 ? 's' : ''}`; } },
            '3-kinases': { estimate: 10, format(x) { return `~${x} min`; } },
        };
        const { estimate, format } = PER_KINASE_ESTIMATES[libraryKey] || {};
        if (!estimate) return null;
        const total = estimate * compoundCount;
        return format(total);
    }
    return null;
}

/**
 * Component to display the estimated time for a screening job,
 * based on the screen type, library, and compound count.
 * @param {{screenTypeKey: string, libraryKey: string, compoundCount: number}} param0
 * @returns {JSX.Element|null}
 */
function EstimatedTime({ screenTypeKey, libraryKey, compoundCount }) {
    const estimate = estimateTime(screenTypeKey, libraryKey, compoundCount);
    if (!estimate) return null;
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0 1rem 0.5rem 1rem',
            fontStyle: 'italic',
        }}
        >
            <AccessTime fontSize="small" />
            {`Estimated time: ${estimate}`}
        </Box>
    );
}
